import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ConflictBadge.css';

const STATE_LABEL = {
  en_uso:      'En uso',
  apartado:    'Apartado',
  reservado:   'Reservado',
  en_locacion: 'En locación',
};

function fmtDate(dateStr) {
  if (!dateStr) return '';
  // dateStr puede venir como "YYYY-MM-DD" o como timestamp ISO completo
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' });
}

export function Popup({ conflict, anchorRect, onClose, onEventClick, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const W = 280;
  let left = anchorRect.left;
  if (left + W > vw - 8) left = vw - W - 8;
  if (left < 8) left = 8;
  const spaceBelow = vh - anchorRect.bottom - 8;
  const estH = Math.min(300, 48 + conflict.conflicts.length * 64);
  const top = spaceBelow >= estH
    ? anchorRect.bottom + 4
    : anchorRect.top - estH - 4;

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="cb-popup"
      style={{ left, top, width: W }}
      onMouseDown={e => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="cb-popup-header">
        <span className="cb-popup-title">{conflict.item_name}</span>
        <span className="cb-popup-stats">
          {conflict.disponible} de {conflict.total} disponibles
        </span>
        {conflict.deficit > 0 && (
          <span className="cb-popup-deficit">Faltan {conflict.deficit} para completar el pedido</span>
        )}
      </div>
      <div className="cb-popup-list">
        {conflict.conflicts.map((c, i) => (
          <button
            key={i}
            className={`cb-popup-row cb-popup-row--${c.state}`}
            onClick={() => { onEventClick(c); onClose(); }}
          >
            <div className="cb-popup-row-top">
              <span className="cb-popup-state">{STATE_LABEL[c.state] || c.state}</span>
              {c.event_date && <span className="cb-popup-date">{fmtDate(c.event_date)}</span>}
            </div>
            <div className="cb-popup-event-name">{c.event_name}</div>
            {c.units?.length > 0 && (
              <div className="cb-popup-units">
                Unidades: {c.units.join(', ')}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}

export default function ConflictBadge({ conflict, onEventClick }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect();
    if (r) { setRect(r); setOpen(o => !o); }
  }, []);

  // Sólo avisar cuando NO quedan unidades libres (disponible llegó a 0);
  // mientras sobre stock, el co-uso del mismo modelo no es conflicto.
  if (!conflict || conflict.disponible !== 0) return null;

  const isBlocking = conflict.en_uso > 0;
  const label = isBlocking
    ? `${conflict.en_uso} en uso`
    : `${conflict.disponible}/${conflict.total}`;

  return (
    <>
      <button
        ref={btnRef}
        className={`cb-badge ${isBlocking ? 'cb-badge--red' : 'cb-badge--yellow'}`}
        onClick={handleClick}
        title={isBlocking ? 'Equipo en uso — no disponible' : 'Conflicto de disponibilidad'}
      >
        {isBlocking ? '🔴' : '⚠'} {label}
      </button>
      {open && rect && (
        <Popup
          conflict={conflict}
          anchorRect={rect}
          onClose={() => setOpen(false)}
          onEventClick={onEventClick}
        />
      )}
    </>
  );
}
